/**
 * Client-side refresh dedup wrapper.
 *
 * Multiple callers (pre-emptive interceptor + reactive 401 + provider hydrate)
 * có thể trigger refresh đồng thời — dedup vào 1 Promise duy nhất.
 *
 * Server-side `/api/auth/refresh` cũng dedup riêng (Map keyed by refresh_token)
 * — đây là layer thứ 2 phòng concurrent request.
 *
 * Doc reference: refesh-token.md §5 (race-safe pattern).
 */

import { getCookie } from 'cookies-next';
import { decodeJwtExp } from './decode-jwt';

const REFRESH_THRESHOLD_SEC = 30;
// Hard cap so a stalled refresh request can never hang auth hydration forever
// (the symptom: user returns after a while → infinite "loading user" until reload).
const REFRESH_TIMEOUT_MS = 10_000;

let inFlight: Promise<boolean> | null = null;

/**
 * Trigger refresh via internal API route. Dedup across all concurrent callers.
 * Always settles within REFRESH_TIMEOUT_MS (aborts a stalled request → false).
 */
export function triggerRefresh(): Promise<boolean> {
  if (inFlight) return inFlight;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

  inFlight = fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    signal: controller.signal,
  })
    .then((r) => r.ok)
    .catch(() => false) // network error OR abort (timeout) → treat as failed
    .finally(() => {
      clearTimeout(timeoutId);
      // Invalidate exp cache (token changed)
      cachedExp = null;
      cachedTokenSig = null;
      setTimeout(() => {
        inFlight = null;
      }, 50);
    });

  return inFlight;
}

/* ─── JWT exp cache ────────────────────────────────────────────────
 * Avoid decode JWT every API request. Cache exp keyed by token signature
 * (first 8 chars). When token changes (refresh), signature changes → invalidate.
 */
let cachedExp: number | null = null;
let cachedTokenSig: string | null = null;

function getCachedExp(token: string): number | null {
  const sig = token.slice(0, 8);
  if (cachedTokenSig === sig && cachedExp !== null) return cachedExp;
  const exp = decodeJwtExp(token);
  if (exp) {
    cachedExp = exp;
    cachedTokenSig = sig;
  }
  return exp;
}

/**
 * Pre-emptive check: refresh nếu token sắp expire (< 30s remaining).
 * Optimized: cache JWT exp để tránh decode mỗi API request.
 */
export async function ensureValidToken(): Promise<string | null> {
  const token = (getCookie('token') as string | undefined) ?? null;
  if (!token) return null;

  const exp = getCachedExp(token);
  if (!exp) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  if (exp - nowSec >= REFRESH_THRESHOLD_SEC) {
    return token; // còn dư > 30s, fast path — không decode/refresh
  }

  // Sắp expire — refresh trước khi dùng
  const ok = await triggerRefresh();
  if (!ok) return null;

  return (getCookie('token') as string | undefined) ?? null;
}
